/**
 * /speaking-time-calculator (SEO_BRIEF_11 section 2.1): how long a text takes to say, and how
 * many words fit in a set time. Pure functions; the page (web/static/base/src/pages/calculator.ts)
 * reads the boxes and writes the answers as the visitor types.
 *
 *   words to time   seconds = words / pace × 60, shown as minutes and seconds
 *   time to words   words = seconds / 60 × pace, rounded to a whole word
 *
 * A WORD is a run of characters between spaces with at least one letter or digit in it, the rule
 * word processors count by: "don't", "e-mail" and "2026" are one word each, and a dash or a
 * bullet standing alone is none. A pace is words a minute, from MIN_PACE to MAX_PACE.
 */

/** The three paces the page offers, in words a minute (section 2.1). */
export const PACES = { slow: 110, average: 140, fast: 170 } as const;
export type PaceName = keyof typeof PACES;
/** The pace a page opens on. */
export const DEFAULT_PACE: PaceName = 'average';

/** The slowest and fastest pace a visitor may type. */
export const MIN_PACE = 20;
export const MAX_PACE = 500;

/** The largest word count read, about two weeks of talking at an average pace. */
export const MAX_WORDS = 3_000_000;
/** The longest time read, in minutes: a week. */
export const MAX_MINUTES = 10_080;

const HAS_WORD_CHARACTER = /[\p{L}\p{N}]/u;

/** The words in `text`, by the rule at the top of this file. */
export function countWords(text: string): number {
  let n = 0;
  for (const token of text.split(/\s+/u)) {
    if (token !== '' && HAS_WORD_CHARACTER.test(token)) n++;
  }
  return n;
}

/**
 * A word count as a visitor types it: digits, with or without thousands separators ("1200",
 * "1,200", "1.200", "1 200"). Null for anything else, a negative or a count over MAX_WORDS.
 */
export function parseWordCount(value: string): number | null {
  const s = value.trim();
  if (!/^\d+$/.test(s) && !/^\d{1,3}(?:[,.   ]\d{3})+$/.test(s)) return null;
  const n = Number(s.replace(/\D/g, ''));
  return Number.isSafeInteger(n) && n <= MAX_WORDS ? n : null;
}

/** A number typed with a dot or a comma for the decimals, or null. */
function parseDecimal(value: string): number | null {
  const s = value.trim().replace(',', '.');
  if (!/^\d+(?:\.\d+)?$|^\.\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** A pace the visitor typed, in words a minute, or null outside MIN_PACE to MAX_PACE. */
export function parsePace(value: string): number | null {
  const n = parseDecimal(value);
  return n !== null && n >= MIN_PACE && n <= MAX_PACE ? n : null;
}

/**
 * A time the visitor typed as minutes and seconds, in seconds. Either box may be empty (it reads
 * as 0) and seconds over 59 are added on, so "0" and "90" is a minute and a half. Null when a box
 * holds something that is not a number, or the total is over MAX_MINUTES.
 */
export function parseDuration(minutes: string, seconds: string): number | null {
  const m = minutes.trim() === '' ? 0 : parseDecimal(minutes);
  const s = seconds.trim() === '' ? 0 : parseDecimal(seconds);
  if (m === null || s === null) return null;
  const total = m * 60 + s;
  return total <= MAX_MINUTES * 60 ? total : null;
}

/** How long `words` take to say at `pace` words a minute, in seconds (not rounded). */
export function speakingSeconds(words: number, pace: number): number {
  if (!(pace > 0) || !(words > 0)) return 0;
  return (words / pace) * 60;
}

/** How many words fit in `seconds` at `pace` words a minute, to the nearest word. */
export function wordsInTime(seconds: number, pace: number): number {
  if (!(pace > 0) || !(seconds > 0)) return 0;
  return Math.round((seconds / 60) * pace);
}

/** A time in seconds as whole minutes and seconds, rounded to the nearest second. */
export function minutesAndSeconds(totalSeconds: number): { minutes: number; seconds: number } {
  const s = Math.max(0, Math.round(totalSeconds));
  return { minutes: Math.floor(s / 60), seconds: s % 60 };
}
